// ======================================================================
// File: src/modules/ppe/pages/procurement/grn/list/GRNListPage.jsx
// Module: PPE → Procurement → GRN
//
// Purpose:
// - Main GRN listing screen
// - Follows same layout pattern as IndentListPage.jsx
// - Shows KPI bar, filter bar, table and pagination
// ======================================================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../grn-ui.css";

import useGRNList from "../hooks/useGRNList";

import GRNFilterBar from "./GRNFilterBar";
import grnColumns from "./grnColumns";
import GRNKpiBar from "./GRNKpiBar";

import GRNFormDrawer from "../form/GRNFormDrawer";

export default function GRNListPage() {

  const navigate = useNavigate();

  // --------------------------------------------------
  // LIST DATA USING CUSTOM HOOK
  // --------------------------------------------------
  const {
    items,
    total,
    loading,
    page,
    pageSize,
    setPage,
    filters,
    setFilters,
    reload,
  } = useGRNList();

  // --------------------------------------------------
  // DRAWER STATE (SAME PATTERN AS INDENT MODULE)
  // --------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create"); // create | edit
  const [selectedGRN, setSelectedGRN] = useState(null);

  const canCreate = true;

  // --------------------------------------------------
  // DRAWER HANDLERS
  // --------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedGRN(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedGRN(null);
  };

  // --------------------------------------------------
  // UI RENDER
  // --------------------------------------------------
  return (
    <div>

      {/* ====================================================== */}
      {/* HEADER SECTION */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #1e3a8a 0%, #2563eb 50%, #60a5fa 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>GRN Management</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Goods receipt processing and inventory intake
          </div>
        </div>

        {canCreate && (
          <button
            className="btn btn-light"
            onClick={openCreateDrawer}
          >
            + Create GRN
          </button>
        )}
      </div>

      {/* ====================================================== */}
      {/* KPI SUMMARY BAR */}
      {/* ====================================================== */}
      <GRNKpiBar rows={items} />

      {/* ====================================================== */}
      {/* FILTER BAR */}
      {/* ====================================================== */}
      <GRNFilterBar
        filters={filters}
        onChange={setFilters}
        onSearch={reload}
      />

      {/* ====================================================== */}
      {/* TABLE LIST */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                {grnColumns.map((c) => (
                  <th key={c.key}>{c.title}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-muted"
                  >
                    No GRN records found
                  </td>
                </tr>
              )}

              {!loading &&
                items.map((row) => (
                  <tr key={row.id}>
                    {grnColumns.map((col) => (
                      <td key={col.key}>
                        {col.render
                          ? col.render(row, { navigate })
                          : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================================================== */}
      {/* PAGINATION */}
      {/* ====================================================== */}
      <div className="erp-pagination">
        <span>
          Page {page} of {Math.ceil(total / pageSize)}
        </span>

        <button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          Prev
        </button>

        <button
          disabled={page * pageSize >= total}
          onClick={() => setPage(page + 1)}
        >
          Next
        </button>
      </div>

      {/* ====================================================== */}
      {/* CREATE GRN DRAWER */}
      {/* ====================================================== */}
      <GRNFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedGRN}
        onClose={closeDrawer}
        onSaved={reload}
      />

    </div>
  );
}
